import type { Supplier } from '../../../types'
import { scoreValue } from '../supplierRiskRatingsTable'
import {
  collapsedBand,
  type CollapsedSupplierRisk,
  type RiskBand,
} from '../supplierRollup'
import type { CubeParentSpend } from './briefCubeSpend'

const TOP_SPEND = 10
const TOP_COUNTRIES = 5

const DRIVING = [
  { key: 'financial', label: 'Financial' },
  { key: 'operational', label: 'Operational' },
  { key: 'compliance', label: 'Legal' },
  { key: 'supply_chain', label: 'Supply chain' },
] as const

/** Do not include bare "na" — that is the North America region code. */
const PLACEHOLDER = new Set(['not available', 'not classified', 'unavailable', 'n/a', 'tbd', ''])

export type SpendBarModel = {
  key: string
  label: string
  spend: number
  sharePct: number
  band: RiskBand | 'unscored'
  singleSource: boolean
  supplierIds: string[]
  rest: boolean
}

export type BriefPicture = {
  spendBars: SpendBarModel[]
  concentrationCaption: string | null
  driving: { label: string; count: number }[]
  drivingNote: string | null
  years: { year: number; spend: number }[] | null
  countries: { key: string; label: string; spend: number }[]
  namedSupplierIds: Set<string>
}

export function bandBarClass(band: RiskBand | 'unscored'): string {
  if (band === 'high') return 'bg-es-riskHigh'
  if (band === 'medium') return 'bg-es-riskMedium'
  if (band === 'low') return 'bg-es-riskLow'
  return 'bg-es-muted/50'
}

export function supplierForParent(
  collapsed: CollapsedSupplierRisk[],
  rows: Supplier[],
  parentName: string
): CollapsedSupplierRisk | undefined {
  const key = parentName.trim().toLowerCase()
  const direct = collapsed.find((row) => row.name.trim().toLowerCase() === key)
  if (direct) return direct
  const line = rows.find(
    (row) =>
      (row.parent_name || '').trim().toLowerCase() === key ||
      (row.name || '').trim().toLowerCase() === key
  )
  if (!line?.name) return undefined
  const legal = line.name.trim().toLowerCase()
  return collapsed.find((row) => row.name.trim().toLowerCase() === legal)
}

export function buildBriefPicture(input: {
  collapsed: CollapsedSupplierRisk[]
  rows: Supplier[]
  parents: CubeParentSpend[]
  byYear: { year: number; spend: number }[]
  supplierCount: number
}): BriefPicture {
  const { collapsed, rows, parents, byYear, supplierCount } = input
  const totalSpend = parents.reduce((sum, row) => sum + row.spend, 0)
  const ranked = parents.map((line) => {
    const supplier = supplierForParent(collapsed, rows, line.parentName)
    const band: RiskBand | 'unscored' = (supplier ? collapsedBand(supplier) : null) ?? 'unscored'
    return {
      line,
      supplier,
      band,
      ids: supplier?.productLineIds ?? [],
    }
  })

  const shown = ranked.slice(0, TOP_SPEND)
  const tail = ranked.slice(TOP_SPEND)
  const spendBars: SpendBarModel[] = shown.map((item) => ({
    key: item.line.parentName,
    label: item.line.parentName,
    spend: item.line.spend,
    sharePct: sharePct(item.line.spend, totalSpend),
    band: item.band,
    singleSource: item.supplier?.single_source === true,
    supplierIds: item.ids,
    rest: false,
  }))
  if (tail.length > 0) {
    const restSpend = tail.reduce((sum, item) => sum + item.line.spend, 0)
    spendBars.push({
      key: '__rest__',
      label: 'Everyone else',
      spend: restSpend,
      sharePct: sharePct(restSpend, totalSpend),
      band: 'unscored',
      singleSource: false,
      supplierIds: tail.flatMap((item) => item.ids),
      rest: true,
    })
  }

  const namedSupplierIds = new Set<string>()
  for (const bar of spendBars) {
    if (bar.rest) continue
    for (const id of bar.supplierIds) namedSupplierIds.add(id)
  }
  for (const supplier of collapsed) {
    if (collapsedBand(supplier) !== 'high') continue
    for (const id of supplier.productLineIds) namedSupplierIds.add(id)
  }

  const shownIds = new Set(shown.flatMap((item) => item.ids))
  let singleSourceOutside = 0
  if (tail.length > 0) {
    for (const supplier of collapsed) {
      if (supplier.single_source !== true) continue
      if (supplier.productLineIds.some((id) => shownIds.has(id))) continue
      singleSourceOutside += 1
    }
  }

  const reach = reachEighty(parents, supplierCount)
  const outside =
    singleSourceOutside > 0
      ? `${singleSourceOutside} other single-source ${
          singleSourceOutside === 1 ? 'supplier sits' : 'suppliers sit'
        } outside the top ${TOP_SPEND}.`
      : null
  const concentrationCaption =
    parents.length === 0 ? null : [reach, outside].filter(Boolean).join(' ')

  const drivingCounts = new Map<string, number>(DRIVING.map((dim) => [dim.key, 0]))
  let highCount = 0
  let scoredHigh = 0
  for (const supplier of collapsed) {
    if (collapsedBand(supplier) !== 'high') continue
    highCount += 1
    const row = representativeRow(rows, supplier)
    if (!row) continue
    let bestKey: string | null = null
    let best = -Infinity
    for (const dim of DRIVING) {
      const value = scoreValue(row, dim.key)
      if (value == null || value <= best) continue
      best = value
      bestKey = dim.key
    }
    if (!bestKey) continue
    scoredHigh += 1
    drivingCounts.set(bestKey, (drivingCounts.get(bestKey) ?? 0) + 1)
  }
  const driving = DRIVING.map((dim) => ({ label: dim.label, count: drivingCounts.get(dim.key) ?? 0 }))
  const drivingNote =
    highCount === 0
      ? 'No high-risk suppliers in this category.'
      : scoredHigh === 0
        ? 'Dimension scores are not stored for these high-risk suppliers.'
        : null

  const years = yearPair(byYear)
  const countries = countryBars(ranked)

  return {
    spendBars: totalSpend > 0 ? spendBars : [],
    concentrationCaption: totalSpend > 0 ? concentrationCaption : null,
    driving,
    drivingNote,
    years,
    countries,
    namedSupplierIds,
  }
}

function sharePct(spend: number, total: number): number {
  if (total <= 0) return 0
  return Math.round((spend / total) * 100)
}

function reachEighty(parents: CubeParentSpend[], supplierCount: number): string {
  const total = parents.reduce((sum, row) => sum + row.spend, 0)
  if (total <= 0 || supplierCount <= 0) return ''
  let cumulative = 0
  let n = 0
  for (const row of parents) {
    n += 1
    cumulative += row.spend
    if (cumulative >= total * 0.8) break
  }
  return `${n} of ${supplierCount} suppliers reach 80% of spend.`
}

function representativeRow(rows: Supplier[], supplier: CollapsedSupplierRisk): Supplier | undefined {
  const ids = new Set(supplier.productLineIds)
  let best: Supplier | undefined
  let bestScore = -Infinity
  for (const row of rows) {
    if (!ids.has(row.id)) continue
    const score = row.overall_score == null ? -1 : Number(row.overall_score)
    if (!best || score > bestScore) {
      best = row
      bestScore = score
    }
  }
  return best
}

function yearPair(byYear: { year: number; spend: number }[]): { year: number; spend: number }[] | null {
  if (byYear.length < 2) return null
  const latest = Math.max(...byYear.map((row) => row.year))
  const current = byYear.find((row) => row.year === latest)
  const prior = byYear.find((row) => row.year === latest - 1)
  if (!current || !prior || current.spend === 0 || prior.spend === 0) return null
  return [prior, current]
}

function countryBars(
  ranked: { line: CubeParentSpend; supplier: CollapsedSupplierRisk | undefined }[]
): { key: string; label: string; spend: number }[] {
  const totals = new Map<string, number>()
  for (const item of ranked) {
    const place = placeOf(item.supplier)
    if (!place) continue
    totals.set(place, (totals.get(place) ?? 0) + item.line.spend)
  }
  const ordered = [...totals.entries()]
    .map(([label, spend]) => ({ key: label, label, spend }))
    .filter((row) => row.spend > 0)
    .sort((a, b) => b.spend - a.spend || a.label.localeCompare(b.label))
  if (ordered.length <= 1) return ordered
  if (ordered.length <= TOP_COUNTRIES) return ordered
  const head = ordered.slice(0, TOP_COUNTRIES)
  const restSpend = ordered.slice(TOP_COUNTRIES).reduce((sum, row) => sum + row.spend, 0)
  head.push({ key: '__rest_country__', label: 'Everywhere else', spend: restSpend })
  return head
}

function placeOf(supplier: CollapsedSupplierRisk | undefined): string | null {
  const raw = (supplier?.location || supplier?.kenvue_region || '').trim()
  if (PLACEHOLDER.has(raw.toLowerCase())) return null
  return raw || null
}
